import { useUser } from '@descope/react-sdk';
import type { OrganizationMember } from '@kirin/api-client/contracts/organization-members';
import { OrganizationRole } from '@kirin/api-client/contracts/organizations';
import {
  canModifyMember,
  countActiveOwners,
} from '@kirin/api-client/helpers/organization-members';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useUpdateMemberRole } from '@/hooks/api/useMembers';
import { useToast } from '@/hooks/use-toast';
import { getAuthContextFromDescope } from '@/lib/descope-roles';
import { getHighestRole } from './getHighestRole';
import { RoleBadge } from './RoleBadge';

interface RoleSelectorProps {
  member: OrganizationMember;
  currentUserId: string;
  organizationId: string;
  allMembers: OrganizationMember[];
}

/**
 * Determine if user can change roles and get available roles
 */
function getRolePermissions(params: {
  currentUserRoles: string[];
  targetMemberRoles: string[];
  currentUserId: string;
  targetMemberId: string;
  ownerCount: number;
}): {
  canChange: boolean;
  availableRoles: string[];
  disabledReason?: string;
} {
  const {
    currentUserRoles,
    targetMemberRoles,
    currentUserId,
    targetMemberId,
    ownerCount,
  } = params;

  const isCurrentUserOwner = currentUserRoles
    .map(r => r.toLowerCase())
    .includes(OrganizationRole.Owner.toLowerCase());
  const isTargetOwner = targetMemberRoles
    .map(r => r.toLowerCase())
    .includes(OrganizationRole.Owner.toLowerCase());
  const isSelf = currentUserId === targetMemberId;
  const isSingleOwner = ownerCount === 1;

  // Non-manager/owner cannot change roles
  if (!canModifyMember(currentUserRoles, targetMemberRoles)) {
    return {
      canChange: false,
      availableRoles: [],
      disabledReason: 'Only owners and managers can change roles',
    };
  }

  // Single owner cannot change own role
  if (isSelf && isTargetOwner && isSingleOwner) {
    return {
      canChange: false,
      availableRoles: [],
      disabledReason:
        'As the only owner, you cannot change your own role. Promote another member to owner first.',
    };
  }

  // Manager cannot change owner's role
  if (!isCurrentUserOwner && isTargetOwner) {
    return {
      canChange: false,
      availableRoles: [],
      disabledReason: 'Only owners can modify owner roles',
    };
  }

  // Determine available roles
  const availableRoles: string[] = isCurrentUserOwner
    ? [
        OrganizationRole.Member,
        OrganizationRole.Manager,
        OrganizationRole.Owner,
        OrganizationRole.Support,
      ]
    : [OrganizationRole.Member, OrganizationRole.Manager]; // Manager can't assign owner or support

  return {
    canChange: true,
    availableRoles,
  };
}

/**
 * RoleSelector component - Shows dropdown to change member roles
 * Falls back to RoleBadge when user doesn't have permission
 */
export function RoleSelector({
  member,
  currentUserId,
  organizationId,
  allMembers,
}: RoleSelectorProps) {
  const { user } = useUser();
  const { mutateAsync: updateRole, isPending } = useUpdateMemberRole();
  const { toast } = useToast();

  // Get current user's roles - prefer fresh API data, fallback to JWT
  const currentUserMember = allMembers.find(m => m.id === currentUserId);
  let currentUserRoles: string[];
  if (currentUserMember) {
    currentUserRoles = currentUserMember.roles;
  } else {
    const authContext = getAuthContextFromDescope(user);
    currentUserRoles = authContext.tenants[organizationId]?.roles || [];
  }

  // Get permissions for this member
  const { canChange, availableRoles, disabledReason } = getRolePermissions({
    currentUserRoles,
    targetMemberRoles: member.roles,
    currentUserId,
    targetMemberId: member.id,
    ownerCount: countActiveOwners(allMembers),
  });

  // If user can't change role, show read-only badge
  if (!canChange) {
    const badge = <RoleBadge roles={member.roles} />;

    // Wrap in tooltip if there's a reason
    if (disabledReason) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="inline-block cursor-not-allowed">{badge}</div>
          </TooltipTrigger>
          <TooltipContent>
            <p>{disabledReason}</p>
          </TooltipContent>
        </Tooltip>
      );
    }

    return badge;
  }

  const currentRole = getHighestRole(member.roles);

  const handleRoleChange = async (newRole: string) => {
    if (newRole === currentRole) return; // No change

    try {
      await updateRole({
        organizationId,
        userId: member.id,
        role: newRole,
      });
      toast({
        title: 'Success',
        description: `Role updated to ${newRole}`,
        variant: 'success',
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to update role';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      console.error('Failed to update member role:', error);
    }
  };

  return (
    <Select
      value={currentRole}
      onValueChange={handleRoleChange}
      disabled={isPending}
    >
      <SelectTrigger size="sm" className="w-32">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {availableRoles.map(role => (
          <SelectItem key={role} value={role}>
            {role.charAt(0).toUpperCase() + role.slice(1)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
